import React, { useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { parseSelectFilterOptions } from '../../../util/search';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectCategoryMultipleFilter.module.css';

// Build the tree preserving hierarchy
const buildTree = (options, level = 1) =>
  (options || []).map(opt => ({
    option: opt.option,
    label: opt.label,
    level,
    children: opt.suboptions ? buildTree(opt.suboptions, level + 1) : [],
  }));

// A single row: independent checkbox + independent expand/collapse toggle
const CategoryItem = ({ node, id, expanded, onToggle }) => {
  const { option, label, level, children } = node;
  const hasChildren = children.length > 0;
  const isExpanded = expanded.has(option);
  const fieldName = `categoryLevel${level}`;
  const inputId = `${id}-${option}`;

  return (
    <li className={css.item}>
      <div className={css.itemRow}>
        {/* Checkbox: selecting/deselecting only. The label wraps only the visual box. */}
        <Field name={fieldName} type="checkbox" value={option}>
          {({ input }) => (
            <label className={css.checkboxWrap} htmlFor={inputId}>
              <input {...input} id={inputId} className={css.checkboxInput} />
              <span className={css.checkmark} />
            </label>
          )}
        </Field>

        {/* Label / expand toggle: completely separate from the checkbox */}
        {hasChildren ? (
          <button
            type="button"
            className={css.toggleBtn}
            onClick={() => onToggle(option)}
          >
            <span className={css.arrow}>{isExpanded ? '▼' : '▶'}</span>
            {label}
          </button>
        ) : (
          // For leaf items, clicking the name also toggles the checkbox via htmlFor
          <label htmlFor={inputId} className={css.leafLabel}>
            {label}
          </label>
        )}
      </div>

      {hasChildren && isExpanded ? (
        <ul className={css.subList}>
          {children.map(child => (
            <CategoryItem
              key={child.option}
              node={child}
              id={id}
              expanded={expanded}
              onToggle={onToggle}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};

const SelectCategoryMultipleFilter = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    queryParamNames,
    initialValues,
    onSubmit,
    options,
    showAsPopup,
    contentPlacementOffset = 0,
    getAriaLabel,
    ...rest
  } = props;

  // All subcategory lists start collapsed
  const [expanded, setExpanded] = useState(new Set());

  const toggle = option => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(option) ? next.delete(option) : next.add(option);
      return next;
    });
  };

  const tree = buildTree(options);

  // Build initial form values: { categoryLevel1: [...], categoryLevel2: [...] }
  const namedInitialValues = queryParamNames.reduce((acc, paramName, idx) => {
    const fieldName = `categoryLevel${idx + 1}`;
    const parsed = initialValues?.[paramName]
      ? parseSelectFilterOptions(initialValues[paramName])
      : [];
    return { ...acc, [fieldName]: parsed };
  }, {});

  const hasInitialValues = Object.values(namedInitialValues).some(v => v.length > 0);

  const handleSubmit = values => {
    const result = queryParamNames.reduce((acc, paramName, idx) => {
      const fieldName = `categoryLevel${idx + 1}`;
      const selected = values?.[fieldName] || [];
      return { ...acc, [paramName]: selected.length > 0 ? `has_any:${selected.join(',')}` : null };
    }, {});
    onSubmit(result);
  };

  const selectionCount = Object.values(namedInitialValues).reduce((s, a) => s + a.length, 0);
  const ariaLabelValue = selectionCount > 0 ? `${selectionCount} selected` : '';

  const classes = classNames(rootClassName || css.root, className);

  const content = (
    <ul className={css.list}>
      {tree.map(node => (
        <CategoryItem
          key={node.option}
          node={node}
          id={id}
          expanded={expanded}
          onToggle={toggle}
        />
      ))}
    </ul>
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={label}
      ariaLabel={getAriaLabel(label, ariaLabelValue)}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {content}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={<span className={hasInitialValues ? css.labelSelected : css.label}>{label}</span>}
      ariaLabel={getAriaLabel(label, ariaLabelValue)}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      {content}
    </FilterPlain>
  );
};

export default SelectCategoryMultipleFilter;
